import React, { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FiChevronLeft, FiChevronRight, FiInbox, FiTag } from 'react-icons/fi';
import { Header } from '../layout/Header';
import { useMyPurchasedTickets } from '../../api/tickets';
import { TicketCard } from './TicketCard';

interface MyTicketsPageProps {
  eventId: string | undefined;
}

export const MyTicketsPage: React.FC<MyTicketsPageProps> = ({ eventId }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch, isFetching } = useMyPurchasedTickets({
    eventId,
    page,
  });

  const tickets = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const hasPrev = !!data?.previous;
  const hasNext = !!data?.next;

  return (
    <div className="min-h-screen bg-black text-white pb-28">
      <Header />

      <main className="mx-auto max-w-[1360px] px-4 sm:px-6 lg:px-8 pt-[calc(var(--header-height,5.5rem)+1.75rem)]">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">My Tickets</h1>
          <p className="mt-1 text-sm text-zinc-400">
            All the event tickets you've purchased, in one place.
          </p>
        </div>

        {!eventId ? (
          <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center">
            <h3 className="mb-1 text-lg font-bold text-white">No event specified</h3>
            <p className="text-sm text-zinc-500">
              We couldn't tell which event to show tickets for.
            </p>
          </div>
        ) : isLoading ? (
          <div className="rounded-3xl border border-[#22242a] p-12 text-center text-sm text-zinc-400">
            Loading your tickets…
          </div>
        ) : isError ? (
          <div className="rounded-3xl border border-dashed border-red-900/50 p-12 text-center">
            <h3 className="mb-1 text-lg font-bold text-white">Couldn't load your tickets</h3>
            <p className="mb-4 text-sm text-zinc-500">Please try again.</p>
            <button
              onClick={() => refetch()}
              className="rounded-full border border-[#2e3138] bg-[#18191d] px-6 py-2.5 text-xs font-bold text-white hover:bg-[#24262c] transition cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-white/10 bg-[#0A0C11] py-24 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-white/[0.02]">
              <FiInbox size={28} className="text-zinc-500" />
            </div>
            <h3 className="mb-2 text-lg font-bold text-white">No tickets yet</h3>
            <p className="max-w-xs text-sm text-zinc-400">
              Tickets you purchase for events will show up here.
            </p>
            <Link
              to="/events/public"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-6 py-2.5 text-xs font-bold text-black hover:bg-zinc-200 transition"
            >
              <FiTag size={13} /> Browse Events
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <TicketCard key={ticket.id} ticket={ticket} />
              ))}
            </div>

            {(hasPrev || hasNext) && (
              <div className="flex items-center justify-between border-t border-[#22242a] pt-4">
                <span className="text-xs text-zinc-500">
                  Page {page} · {totalCount} ticket{totalCount === 1 ? '' : 's'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!hasPrev || isFetching}
                    className="flex items-center gap-1 rounded-full border border-[#22242a] px-3 py-2 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <FiChevronLeft size={13} /> Prev
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasNext || isFetching}
                    className="flex items-center gap-1 rounded-full border border-[#22242a] px-3 py-2 text-xs font-semibold text-zinc-300 transition hover:border-zinc-600 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    Next <FiChevronRight size={13} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
